// Everything the Import files screen shows, derived from the files and the user's choices.
// Pure, so the Vue dialog only renders it and every rule is covered by Jest
// (importScreenState.test.ts). Ported from Android's `ImportChoices.kt` and
// `ImportScreenState.kt` (modules/importList/state), which follow the design prototype
// and docs/unified-import-spec.md → "Current design"; the names match so a question
// about one platform reads the same on the other.

import { defaultFor, haversineM } from './importRules';
import type { ImportAction, ImportError, ImportIntent, ImportPoint, ImportPreview, SkipReason } from './importTypes';
import {
  fromWaypointsDay,
  keptWaypoints,
  plannedDays,
  plannedLines,
  recordingDistanceM,
  recordingDurationMs,
  waypointsToAdd
} from './importPayloads';

/**
 * What the user picked for one file in "Import as": the contract's ImportAction plus
 * `editInPlanner`, which hands the lines to the planner instead of saving them.
 */
export type ImportChoice = 'planned' | 'editInPlanner' | 'recording' | 'waypoints' | 'fromWaypoints' | 'skip';

/** Where an imported file ends up; also the start screen an import was opened from. */
export type ImportDestination = 'recordings' | 'plannedHikes' | 'waypoints';

/** Display order everywhere: recordings → planned hikes → waypoints. */
export const IMPORT_DESTINATIONS: ImportDestination[] = ['recordings', 'plannedHikes', 'waypoints'];

export function choiceDestination(choice: ImportChoice): ImportDestination | null {
  switch (choice) {
    case 'planned':
    case 'editInPlanner':
    case 'fromWaypoints':
      return 'plannedHikes';
    case 'recording':
      return 'recordings';
    case 'waypoints':
      return 'waypoints';
    default:
      return null;
  }
}

/** The start screen's own destination; null when the screen was opened with none. */
export function intentDestination(intent: ImportIntent): ImportDestination | null {
  switch (intent) {
    case 'planned':
      return 'plannedHikes';
    case 'recording':
      return 'recordings';
    case 'waypoints':
      return 'waypoints';
    default:
      return null;
  }
}

export function actionToChoice(action: ImportAction): ImportChoice {
  return action === 'fromwpts' ? 'fromWaypoints' : action;
}

/** The per-file settings under "Import as", "Days", "Lines" and "Waypoints". */
export interface ImportFileSettings {
  choice: ImportChoice;
  /** "One day" instead of one day per line. */
  joinDays: boolean;
  /** Also import a route that copies a track in the same file (contract rule 2). */
  includeCourse: boolean;
  /** The file's named waypoints, as highlights or a waypoint group. */
  keepWaypoints: boolean;
  /** The unnamed points that just repeat the line (contract rule 3). */
  includeRepeats: boolean;
}

export const settingsFor = (choice: ImportChoice, overrides: Partial<ImportFileSettings> = {}): ImportFileSettings => ({
  choice,
  joinDays: false,
  includeCourse: false,
  keepWaypoints: true,
  includeRepeats: false,
  ...overrides
});

/**
 * A hike from waypoints' route, planned in the background as soon as that's the file's choice
 * (importRouteRunner.ts) and saved as is. `key` is the waypoint list it was planned through
 * (`routeKey`), so a route for an older list is never mistaken for the current one.
 */
export type WaypointRoute =
  | { key: string; status: 'routing' }
  | { key: string; status: 'routed'; points: ImportPoint[] }
  | { key: string; status: 'failed' };

export type WaypointRouteStatus = WaypointRoute['status'];

/**
 * One file on the screen. `preview` is null until the file has been read. A route file
 * unpacked from a ZIP archive is a file of its own, whose `source` is the ZIP that was added.
 */
export interface ImportFileEntry {
  id: string;
  name: string;
  preview: ImportPreview | null;
  /** What the start screen made of it (contract rule 6); fixed once the file is read. */
  defaultChoice: ImportChoice;
  /** Why the file doesn't fit the start screen (contract `skipReasons`), when it doesn't. */
  skipReason: SkipReason | null;
  settings: ImportFileSettings;
  /** The id of the file the user added: `id` itself, or the ZIP archive this came out of. */
  source: string;
  /** The file's hike-from-waypoints route, once one has been asked for; kept across choices. */
  route?: WaypointRoute | null;
}

export const isLoading = (e: ImportFileEntry): boolean => e.preview === null;
export const entryError = (e: ImportFileEntry): ImportError | null => e.preview?.error ?? null;
export const fromArchive = (e: ImportFileEntry): boolean => e.source !== e.id;

/** The waypoints a hike from waypoints is routed through, in file order. */
export const routeStops = (e: ImportFileEntry): ImportPoint[] =>
  e.preview && !e.preview.error ? fromWaypointsDay(e.preview).points : [];

/** What a route is cached against: the exact waypoint list it goes through. */
export const routeKey = (stops: ImportPoint[]): string => stops.map(p => `${p.lon},${p.lat}`).join(';');

/** The file's route for its waypoints as they are now; null when none has been asked for since. */
export function currentRoute(e: ImportFileEntry): WaypointRoute | null {
  return e.route && e.route.key === routeKey(routeStops(e)) ? e.route : null;
}

/** Where a hike from waypoints' route is at: planning until one for its current waypoints lands. */
export const routeStatus = (e: ImportFileEntry): WaypointRouteStatus => currentRoute(e)?.status ?? 'routing';

/** The file's recommended choice (contract rule 5); null while reading or unreadable. */
export function recommendedChoice(e: ImportFileEntry): ImportChoice | null {
  if (!e.preview || e.preview.error || !e.preview.recommended) return null;
  return actionToChoice(e.preview.recommended);
}

/**
 * What "Import as" starts on: the contract's default, which is the file's own type. Don't
 * import only when the file has nothing it can become.
 */
export function initialChoice(e: ImportFileEntry): ImportChoice {
  return e.defaultChoice === 'skip' ? recommendedChoice(e) ?? 'skip' : e.defaultChoice;
}

/** A file that's still being read. */
export const pendingEntry = (id: string, name: string, source: string = id): ImportFileEntry => ({
  id,
  name,
  preview: null,
  defaultChoice: 'skip',
  skipReason: null,
  settings: settingsFor('skip'),
  source
});

/** A read file's entry: its default choice comes from the start screen (`defaultFor`). */
export function importFileEntry(id: string, name: string, preview: ImportPreview, intent: ImportIntent, source: string = id): ImportFileEntry {
  const { action, reason } = defaultFor(preview, intent);
  const entry: ImportFileEntry = {
    id,
    name,
    preview,
    defaultChoice: actionToChoice(action),
    skipReason: reason,
    settings: settingsFor('skip'),
    source
  };
  return { ...entry, settings: settingsFor(initialChoice(entry)) };
}

/** Where a file sits on a several-file screen. Decided by its default, so it never moves. */
export type ImportFileGroup = 'ready' | 'look' | 'error';

/** The badge on an "Import as" option: only ever the recommended one. */
export type ImportOptionBadge = 'recommended';

/**
 * How many of each thing the import will save. The Edit in planner file isn't saved (the
 * planner gets it), so it's flagged by `editInPlanner` rather than counted as a planned hike.
 */
export interface ImportCounts {
  recordings: number;
  plannedHikes: number;
  waypointGroups: number;
  editInPlanner: boolean;
}

export const countsTotal = (c: ImportCounts): number => c.recordings + c.plannedHikes + c.waypointGroups;

export function countOf(c: ImportCounts, d: ImportDestination): number {
  if (d === 'recordings') return c.recordings;
  if (d === 'plannedHikes') return c.plannedHikes;
  return c.waypointGroups;
}

/** What the action button says. */
export type ImportCta =
  | { kind: 'openInPlanner' }
  | { kind: 'importThenPlanner'; counts: ImportCounts }
  | { kind: 'nothing' }
  | { kind: 'import'; counts: ImportCounts };

/** The one stats line under a file's name. */
export type ImportFileMeta =
  | { kind: 'reading' }
  | { kind: 'unreadable'; error: ImportError }
  | { kind: 'skipped'; reason: SkipReason | null; intent: ImportIntent; needsLook: boolean }
  | { kind: 'recording'; distanceM: number; durationMs: number }
  | { kind: 'planned'; editInPlanner: boolean; days: number; distanceM: number; highlights: number }
  | { kind: 'waypoints'; count: number }
  | { kind: 'fromWaypoints'; count: number; keptAsHighlights: boolean; route: WaypointRouteStatus; distanceM: number };

/** One summary tile: how many of `destination` the import will create right now. */
export interface ImportTile {
  destination: ImportDestination;
  count: number;
}

/** The sticky "N need a look · N can't be read" bar and what it folds away. */
export interface ImportLookBar {
  lookCount: number;
  errorCount: number;
  /** Files behind the bar the user has chosen to import anyway. */
  addedCount: number;
  open: boolean;
}

/** Which settings an open file shows, and the numbers they need. */
export interface ImportFileSections {
  showPickHint: boolean;
  options: ImportChoice[];
  showDays: boolean;
  dayCount: number;
  showLines: boolean;
  showWaypoints: boolean;
  namedWaypoints: number;
  repeatedPoints: number;
  waypointsAsHighlights: boolean;
  /** A hike from waypoints whose route couldn't be planned: offer "Try again". */
  showRouteRetry: boolean;
}

export interface ImportScreenState {
  intent: ImportIntent;
  entries: ImportFileEntry[];
  /** The expanded file, if any. One file starts expanded. */
  openId: string | null;
  /** Whether the user opened the "need a look" bar. */
  lookExpanded: boolean;
  isImporting: boolean;
}

export const emptyScreen = (intent: ImportIntent): ImportScreenState => ({
  intent,
  entries: [],
  openId: null,
  lookExpanded: false,
  isImporting: false
});

export const isSingle = (s: ImportScreenState): boolean => s.entries.length === 1;

export const findEntry = (s: ImportScreenState, id: string): ImportFileEntry | undefined => s.entries.find(e => e.id === id);

// ---- per file -------------------------------------------------------------------------

/** "Import as", in display order. Edit in planner on every file that can be planned (one at a time). */
export function options(s: ImportScreenState, e: ImportFileEntry): ImportChoice[] {
  const p = e.preview;
  if (!p || p.error) return [];
  const out: ImportChoice[] = [];
  if (p.canBe.includes('planned')) out.push('planned');
  if (p.canBe.includes('planned')) out.push('editInPlanner');
  if (p.canBe.includes('recording')) out.push('recording');
  if (p.canBe.includes('waypoints')) out.push('waypoints');
  if (p.canBe.includes('fromwpts')) out.push('fromWaypoints');
  out.push('skip');
  return out;
}

/** Whether the file's type sends it to the start screen; always true with no start screen. */
export function fitsStartScreen(s: ImportScreenState, e: ImportFileEntry): boolean {
  const home = intentDestination(s.intent);
  if (!home) return true;
  return choiceDestination(e.defaultChoice) === home;
}

// A file going somewhere other than the start screen is listed with the rest, tagged with
// where it goes; only a file with nothing it can become waits behind the bar.
export function group(_s: ImportScreenState, e: ImportFileEntry): ImportFileGroup {
  if (entryError(e)) return 'error';
  if (isLoading(e)) return 'ready';
  return e.defaultChoice === 'skip' ? 'look' : 'ready';
}

/** Whether this file creates something when the button is pressed. */
export function willImport(e: ImportFileEntry): boolean {
  const p = e.preview;
  if (!p || p.error) return false;
  if (e.settings.choice === 'skip') return false;
  if (e.settings.choice === 'waypoints') return waypointsToAdd(p, e.settings).length > 0;
  // Counted while its route is being planned (saving waits for it), never once that's failed.
  if (e.settings.choice === 'fromWaypoints') return routeStatus(e) !== 'failed';
  return true;
}

/** The "→ Planned hikes" tag: where the file goes when that isn't the start screen. */
export function crossDestination(s: ImportScreenState, e: ImportFileEntry): ImportDestination | null {
  const home = intentDestination(s.intent);
  if (!home) return null;
  const goesTo = choiceDestination(e.settings.choice);
  return goesTo && goesTo !== home ? goesTo : null;
}

export function badge(s: ImportScreenState, e: ImportFileEntry, choice: ImportChoice): ImportOptionBadge | null {
  // No start screen, or the file's type sends it elsewhere: flag the recommended option.
  // An option that fits the start screen carries no badge.
  if (intentDestination(s.intent) && fitsStartScreen(s, e)) return null;
  return choice !== 'skip' && choice === recommendedChoice(e) ? 'recommended' : null;
}

/** Whether an option reads "Goes to Planned hikes, not Recordings". */
export function goesElsewhere(s: ImportScreenState, choice: ImportChoice): boolean {
  const home = intentDestination(s.intent);
  if (!home || choice === 'skip' || choice === 'editInPlanner') return false;
  return choiceDestination(choice) !== home;
}

export function meta(s: ImportScreenState, e: ImportFileEntry): ImportFileMeta {
  const p = e.preview;
  if (!p) return { kind: 'reading' };
  if (p.error) return { kind: 'unreadable', error: p.error };
  const settings = e.settings;
  switch (settings.choice) {
    case 'skip': {
      const needsLook = group(s, e) === 'look';
      return { kind: 'skipped', reason: needsLook ? e.skipReason : null, intent: s.intent, needsLook };
    }
    case 'recording':
      return { kind: 'recording', distanceM: recordingDistanceM(p), durationMs: recordingDurationMs(p) };
    case 'planned':
    case 'editInPlanner': {
      const edit = settings.choice === 'editInPlanner';
      const effective = edit ? { ...settings, joinDays: false } : settings;
      return {
        kind: 'planned',
        editInPlanner: edit,
        days: plannedDays(p, effective).length,
        distanceM: plannedLines(p, effective).reduce((sum, l) => sum + l.distanceM, 0),
        highlights: waypointsToAdd(p, settings).length
      };
    }
    case 'waypoints':
      return { kind: 'waypoints', count: waypointsToAdd(p, settings).length };
    case 'fromWaypoints': {
      const route = currentRoute(e);
      const points = route?.status === 'routed' ? route.points : [];
      const distanceM = points.reduce((sum, pt, i) => (i ? sum + haversineM(points[i - 1], pt) : 0), 0);
      return { kind: 'fromWaypoints', count: keptWaypoints(p).length, keptAsHighlights: settings.keepWaypoints, route: routeStatus(e), distanceM };
    }
  }
}

export function sections(s: ImportScreenState, e: ImportFileEntry): ImportFileSections {
  const p = e.preview;
  const choice = e.settings.choice;
  const lineCount = p ? plannedLines(p, e.settings).length : 0;
  const named = p?.waypointCounts.kept ?? 0;
  return {
    showPickHint: group(s, e) === 'look',
    options: options(s, e),
    showDays: choice === 'planned' && lineCount > 1,
    dayCount: lineCount,
    showLines: (choice === 'planned' || choice === 'editInPlanner') && !!p && p.lines.some(l => l.duplicateOf !== null),
    showWaypoints: named > 0 && (choice === 'planned' || choice === 'waypoints' || choice === 'fromWaypoints'),
    namedWaypoints: named,
    repeatedPoints: p?.waypointCounts.repeatsLine ?? 0,
    waypointsAsHighlights: choice !== 'waypoints',
    showRouteRetry: choice === 'fromWaypoints' && !!p && !p.error && routeStatus(e) === 'failed'
  };
}

// ---- the whole screen -------------------------------------------------------------------

export function counts(s: ImportScreenState): ImportCounts {
  const importing = s.entries.filter(willImport);
  const saved = importing.filter(e => e.settings.choice !== 'editInPlanner');
  const to = (d: ImportDestination): number => saved.filter(e => choiceDestination(e.settings.choice) === d).length;
  return {
    recordings: to('recordings'),
    plannedHikes: to('plannedHikes'),
    waypointGroups: to('waypoints'),
    editInPlanner: importing.some(e => e.settings.choice === 'editInPlanner')
  };
}

export function cta(s: ImportScreenState): ImportCta {
  const c = counts(s);
  if (c.editInPlanner) return countsTotal(c) === 0 ? { kind: 'openInPlanner' } : { kind: 'importThenPlanner', counts: c };
  if (countsTotal(c) === 0) return { kind: 'nothing' };
  return { kind: 'import', counts: c };
}

function readyOrder(e: ImportFileEntry): number {
  if (isLoading(e)) return 3;
  const d = choiceDestination(e.defaultChoice);
  return d ? IMPORT_DESTINATIONS.indexOf(d) : 3;
}

/** Files the start screen accepts, recordings → planned hikes → waypoints; unread files last. */
export function readyEntries(s: ImportScreenState): ImportFileEntry[] {
  return s.entries
    .map((e, i) => ({ e, i }))
    .filter(({ e }) => group(s, e) === 'ready')
    .sort((a, b) => readyOrder(a.e) - readyOrder(b.e) || a.i - b.i)
    .map(({ e }) => e);
}

export const lookEntries = (s: ImportScreenState): ImportFileEntry[] => s.entries.filter(e => group(s, e) === 'look');

export const errorEntries = (s: ImportScreenState): ImportFileEntry[] => s.entries.filter(e => group(s, e) === 'error');

/** Null when nothing needs folding away. Open when the user opened it, or nothing is ready. */
export function lookBar(s: ImportScreenState): ImportLookBar | null {
  const look = lookEntries(s);
  const errors = errorEntries(s);
  if (!look.length && !errors.length) return null;
  return {
    lookCount: look.length,
    errorCount: errors.length,
    addedCount: look.filter(willImport).length,
    open: s.lookExpanded || readyEntries(s).length === 0
  };
}

/** What a file is: its start-screen destination, or what it naturally is if it didn't fit. */
function tileType(e: ImportFileEntry): ImportDestination | null {
  const p = e.preview;
  if (!p || p.error) return null;
  const d = choiceDestination(e.defaultChoice);
  if (d) return d;
  if (p.canBe.includes('recording')) return 'recordings';
  if (p.lines.length) return 'plannedHikes';
  return 'waypoints';
}

/** One tile per type present, with the count that will be imported. */
export function tiles(s: ImportScreenState): ImportTile[] {
  const present = new Set(s.entries.map(tileType).filter((d): d is ImportDestination => d !== null));
  const c = counts(s);
  return IMPORT_DESTINATIONS.filter(d => present.has(d)).map(d => ({ destination: d, count: countOf(c, d) }));
}

/** Files that will import somewhere other than the start screen; for analytics. */
export const crossDestinationCount = (s: ImportScreenState): number =>
  s.entries.filter(e => willImport(e) && crossDestination(s, e) !== null).length;
